import { writable, derived, get } from 'svelte/store'

export interface SearchMatch {
  index: number
  start: number
  end: number
  text: string
}

export interface SearchState {
  isVisible: boolean
  searchTerm: string
  matches: SearchMatch[]
  currentMatchIndex: number
  isSearching: boolean
}

function createSearchStore() {
  const initialState: SearchState = {
    isVisible: false,
    searchTerm: '',
    matches: [],
    currentMatchIndex: 0,
    isSearching: false
  }
  
  const { subscribe, set, update } = writable<SearchState>(initialState)
  
  return {
    subscribe,
    
    // Show/hide search bar
    show() {
      update(state => ({ ...state, isVisible: true }))
    },
    
    hide() {
      update(state => ({
        ...state,
        isVisible: false,
        searchTerm: '',
        matches: [],
        currentMatchIndex: 0
      }))
    },
    
    toggle() {
      update(state => {
        if (state.isVisible) {
          return {
            ...state,
            isVisible: false,
            searchTerm: '',
            matches: [],
            currentMatchIndex: 0
          }
        }
        return { ...state, isVisible: true }
      })
    },
    
    // Search functionality
    search(text: string, searchTerm: string) {
      if (!searchTerm) {
        update(state => ({
          ...state,
          searchTerm: '',
          matches: [],
          currentMatchIndex: 0,
          isSearching: false
        }))
        return []
      }
      
      update(state => ({ ...state, isSearching: true }))
      
      const matches: SearchMatch[] = []
      const lowerSearchTerm = searchTerm.toLowerCase()
      const lowerText = text.toLowerCase()
      
      let position = 0
      let matchIndex = 0
      
      while (position < lowerText.length) {
        const index = lowerText.indexOf(lowerSearchTerm, position)
        if (index === -1) break
        
        matches.push({
          index: matchIndex++,
          start: index,
          end: index + searchTerm.length,
          text: text.substring(index, index + searchTerm.length)
        })
        
        position = index + 1
      }
      
      update(state => ({
        ...state,
        searchTerm,
        matches,
        currentMatchIndex: matches.length > 0 ? 0 : -1,
        isSearching: false
      }))
      
      return matches
    },
    
    // Navigation
    next() {
      update(state => {
        if (state.matches.length === 0) return state
        
        const nextIndex = (state.currentMatchIndex + 1) % state.matches.length
        return { ...state, currentMatchIndex: nextIndex }
      })
    },
    
    previous() {
      update(state => {
        if (state.matches.length === 0) return state
        
        const prevIndex = state.currentMatchIndex - 1
        const newIndex = prevIndex < 0 ? state.matches.length - 1 : prevIndex
        return { ...state, currentMatchIndex: newIndex }
      })
    },
    
    // Get current match
    getCurrentMatch(): SearchMatch | null {
      const state = get({ subscribe })
      if (state.matches.length === 0 || state.currentMatchIndex < 0) {
        return null
      }
      return state.matches[state.currentMatchIndex]
    },
    
    // Clear search (but keep search bar visible if it was visible)
    clear() {
      update(state => ({
        ...initialState,
        isVisible: state.isVisible  // Preserve visibility state
      }))
    },
    
    // Set search term without performing search
    setSearchTerm(term: string) {
      update(state => ({
        ...state,
        searchTerm: term
      }))
    },
    
    // Navigate to specific match index
    goToMatch(index: number) {
      update(state => {
        if (state.matches.length === 0 || index < 0 || index >= state.matches.length) {
          return state
        }
        return { ...state, currentMatchIndex: index }
      })
    }
  }
}

export const searchStore = createSearchStore()

// Derived store for current match info
export const currentMatch = derived(searchStore, $search => {
  if ($search.matches.length === 0 || $search.currentMatchIndex < 0) {
    return null
  }
  return $search.matches[$search.currentMatchIndex]
})

// Derived store for search stats
export const searchStats = derived(searchStore, $search => ({
  current: $search.currentMatchIndex + 1,
  total: $search.matches.length,
  hasMatches: $search.matches.length > 0
}))